import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, PenSquare, Sparkles } from 'lucide-react';
import { getQuizzes } from '@/lib/api';
import { formatDate, formatNumber } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export default function QuizzesPage() {
  const [quizzes, setQuizzes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getQuizzes();
        setQuizzes(data || []);
      } catch (error) {
        console.error('Failed to fetch quizzes:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          <p className="mt-4 text-sm text-muted-foreground">Loading quizzes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <section className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-2xl font-semibold text-foreground">
            <Sparkles className="h-6 w-6 text-muted-foreground" aria-hidden />
            Quiz Templates
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Author, localize, and publish quiz templates for cyber range
            training sessions.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button asChild variant="gradient">
            <Link to="/quizzes/new">
              <PenSquare className="h-4 w-4" aria-hidden />
              Add or edit quizzes
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/questions">
              Question bank
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
          </Button>
        </div>
      </section>

      <section className="rounded-xl border border-border bg-card p-6 shadow-card">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h3 className="flex items-center gap-2 text-lg font-semibold text-card-foreground">
              <PenSquare className="h-5 w-5 text-muted-foreground" aria-hidden />
              Published quizzes
            </h3>
            <p className="text-sm text-muted-foreground">
              {quizzes.length === 0
                ? "No quizzes found. Start authoring to populate this list."
                : `${formatNumber(quizzes.length)} available quiz templates.`}
            </p>
          </div>
        </div>

        <div className="mt-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Questions</TableHead>
                <TableHead>Updated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {quizzes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground">
                    Awaiting quizzes. Use the "Add or edit" button above to get started.
                  </TableCell>
                </TableRow>
              ) : (
                quizzes.map((quiz) => (
                  <TableRow key={quiz.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <PenSquare className="h-4 w-4 text-muted-foreground" aria-hidden />
                        {quiz.name}
                      </div>
                    </TableCell>
                    <TableCell>{quiz.description || "—"}</TableCell>
                    <TableCell>{formatNumber(quiz.questions.length)}</TableCell>
                    <TableCell>{formatDate(quiz.updated_at)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </section>
    </div>
  );
}

